import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import moment from "moment";

// custom components
import ClassFilter from "pages/book_a_class/ClassFilter";
import ClassSearchResult from "pages/book_a_class/ClassSearchResult";

// graphql
import GET_CURRENT_USER_INFO from "graphql/queries/GetCurrentUserInfo";
import GET_CATEGORIES from "graphql/queries/GetCategories";
import GET_CITIES from "graphql/queries/GetCities";
import GET_LANGUAGES from "graphql/queries/GetLanguages";

// custom types
import {
  Category,
  City,
  CategoryState,
  CityState,
  DayState,
  Language,
} from "types";
// styling
import * as Layout from "common/layout";
import { SectionTitle } from "common/text";
import useBannerBackground from "hooks/useBannerBackground";
import { getLevelId, setIsChineseLanguage } from "utils/language";
import LanguagesFilter from "./LanguagesFilter";
import LevelRecommendation from "./LevelRecommendation";

// utils

import { BookClassSection } from "./BookAClassStyle";

const { PageContent, SectionHeader, HeaderCol } = Layout;

const BookAClass = () => {
  const { t } = useTranslation("book_a_class");

  const enMoment = moment();
  enMoment.locale("en");

  const { data: currentUserInfo } = useQuery(GET_CURRENT_USER_INFO, {
    fetchPolicy: "network-only",
  });
  const { data: categoryList } = useQuery(GET_CATEGORIES);
  const { data: cityList } = useQuery(GET_CITIES);
  const { data: languageList } = useQuery(GET_LANGUAGES);
  // STATE MANAGEMENT FOR CLASS FILTERS

  const [filter, setFilter] = useState({
    level: {
      id: "0",
    },
    classType: "small_group",
    days: enMoment
      .localeData()
      .weekdaysShort()
      .map((wd) => ({
        name: wd,
        chosen: true,
      })) as DayState[],
    time: [0, 23],
    includeStudiedTopics: false,
    excludeAllCityVerion: true,
    categories: [] as CategoryState[],
    spokenOnly: false,
    numberOfTopicsAhead: 50,
    charactersPreference: "simplified",
    cities: [] as CityState[],
    language: {
      id: "1",
    },
    isChineseLanguage: false,
  });

  // For changes in day, category, and city fields of filter
  const onChangeDaysCategoriesCities = (
    field: "days" | "categories" | "cities",
    name: string
  ) => {
    // get the corresponding option array
    const stateArray: DayState[] | CategoryState[] | CityState[] =
      filter[field];
    // find the index of the appropriate option
    const optionIndex = stateArray.findIndex(
      (option: DayState | CategoryState | CityState) => {
        return option.name === name;
      }
    );
    // update the new option
    stateArray[optionIndex].chosen = !stateArray[optionIndex].chosen;
    // set state using the updated array
    if (field === "days") {
      setFilter({
        ...filter,
        days: stateArray as DayState[],
      });
    } else if (field === "categories") {
      setFilter({
        ...filter,
        categories: stateArray as CategoryState[],
      });
    } else if (field === "cities") {
      setFilter({
        ...filter,
        cities: stateArray as CityState[],
      });
    }
  };

  // For changes in the following field: level, class type, time, include studied topics,
  // spoken only, # of topics ahead, characters
  const onChangeOtherFilterFields = (
    field: string,
    value: string | number | number[] | boolean
  ) => {
    if (field === "level") {
      setFilter({
        ...filter,
        level: {
          id: value as string,
        },
      });
    } else if (field === "classType") {
      setFilter({
        ...filter,
        classType: value as string,
      });
    } else if (field === "time") {
      setFilter({
        ...filter,
        time: value as number[],
      });
    } else if (field === "includeStudiedTopics") {
      let includeStudiedTopics = false;
      let excludeAllCityVerion = false;
      if (value === 1) {
        includeStudiedTopics = false;
        excludeAllCityVerion = true;
      } else if (value === 0) {
        includeStudiedTopics = false;
        excludeAllCityVerion = false;
      } else {
        includeStudiedTopics = true;
        excludeAllCityVerion = true;
      }
      setFilter({
        ...filter,
        includeStudiedTopics,
        excludeAllCityVerion,
      });
    } else if (field === "spokenOnly") {
      const categoryArray: CategoryState[] = filter.categories;
      // if value is true, unselect all categories marked as skippedIfSpokenOnly
      // if value is false, re-select all categories NOT marked as skippedIfSpokenOnly
      const booleanValue = value === "true";
      categoryArray.forEach((category: CategoryState) => {
        if (category.skippedIfSpokenOnly) {
          // eslint-disable-next-line no-param-reassign
          category.chosen = !booleanValue;
        }
      });
      setFilter({
        ...filter,
        spokenOnly: booleanValue,
        categories: categoryArray,
      });
    } else if (field === "numberOfTopicsAhead") {
      setFilter({
        ...filter,
        numberOfTopicsAhead: value as number,
      });
    } else if (field === "charactersPreference") {
      const cityArray: CityState[] = filter.cities;
      cityArray.forEach((city: CityState) => {
        if (value === "traditional") {
          // eslint-disable-next-line no-param-reassign
          city.chosen = city.name === "TP";
        } else {
          // eslint-disable-next-line no-param-reassign
          city.chosen = true;
        }
      });
      setFilter({
        ...filter,
        charactersPreference: value as string,
      });
    } else if (field === "language") {
      const isChineseLanguage = setIsChineseLanguage(
        value as string,
        languageList.languages
      );
      const levelId = getLevelId(
        value as string,
        languageList.languages,
        currentUserInfo.me.savedLevels?.find(
          (level: { id: string; languageId: string }) =>
            level.languageId === value
        )?.id || currentUserInfo.me.currentLevel.id
      );
      const cities = languageList.languages
        .find((language: Language) => language.id === value)
        .cities.map((city: City) => ({
          id: city.id,
          name: city.name,
          // all cities are chosen by default
          chosen: true,
        }));
      const specifyFieldChinese = {};
      if (!isChineseLanguage) {
        const categoryArray: CategoryState[] = filter.categories;
        categoryArray.forEach((category: CategoryState) => {
          if (category.skippedIfSpokenOnly) {
            // eslint-disable-next-line no-param-reassign
            category.chosen = true;
          }
        });
        Object.assign(specifyFieldChinese, {
          spokenOnly: false,
          charactersPreference: "simplified",
          categories: categoryArray,
        });
      }

      setFilter({
        ...filter,
        level: {
          id: levelId as string,
        },
        language: {
          id: value as string,
        },
        isChineseLanguage,
        cities,
        ...specifyFieldChinese,
      });
    }
  };

  const { bannerBackgroundUrl, refetchBannerBackground } =
    useBannerBackground();
  const setDefaultClassType = (preferClassType: number | undefined) => {
    return !preferClassType ? "small_group" : "one_on_one";
  };
  // By default, level id is the user's current level,
  // spokenOnly is based on user's choice duing registration,
  // and all appropriate categories and cities are selected.

  if (
    filter.level.id === "0" &&
    currentUserInfo &&
    categoryList &&
    cityList &&
    languageList
  ) {
    const currentLevelId = currentUserInfo.me.currentLevel.id;
    const { charactersPreference, spokenOnly } = currentUserInfo.me;
    const categories = categoryList.categories.map((category: Category) => ({
      id: category.id,
      name: category.name,
      skippedIfSpokenOnly: category.skippedIfSpokenOnly,
      // it's chosen by default if the categoru is not marked as skippedIfSpokenOnly
      // or if the user did NOT choose spoken only
      chosen: !category.skippedIfSpokenOnly || !spokenOnly,
    }));
    const cities = languageList.languages
      .find(
        (language: Language) =>
          language.id === currentUserInfo.me.currentLanguage.id
      )
      .cities.map((city: City) => ({
        id: city.id,
        name: city.name,
        // all cities are chosen by default if filter.charactersPreference === "simplified"
        chosen:
          charactersPreference === "simplified" ||
          (charactersPreference === "traditional" && city.name === "TP"),
      }));

    const defaultClassType = setDefaultClassType(
      currentUserInfo.me.preferClassType
    );

    const currentLanguageId = currentUserInfo.me.currentLanguage.id;
    const isChineseLanguage = setIsChineseLanguage(
      currentLanguageId,
      languageList.languages
    );

    setFilter({
      ...filter,
      level: {
        id: currentLevelId as string,
      },
      classType: defaultClassType,
      spokenOnly,
      categories,
      cities,
      charactersPreference,
      language: {
        id: currentLanguageId as string,
      },
      isChineseLanguage,
    });
  }

  const activeLanguage = languageList?.languages.find(
    (language: Language) => language.id === filter.language.id
  );

  const bookClassTitle = t(`BOOK A {{ language }} CLASS`, {
    language: activeLanguage?.englishName?.toUpperCase() || "",
  });

  const fixFilter = {
    ...filter,
    time: [filter.time[0], filter.time[1] - 1],
  };
  return (
    <PageContent>
      {activeLanguage && filter.level.id !== "0" && (
        <LevelRecommendation
          languageId={filter.language.id}
          languageName={activeLanguage.englishName}
          backgroundUrl={bannerBackgroundUrl}
        />
      )}

      <BookClassSection>
        <SectionHeader>
          <HeaderCol xs={24} sm={24} md={18}>
            <SectionTitle>{bookClassTitle}</SectionTitle>
          </HeaderCol>
          <HeaderCol xs={24} sm={24} md={6}>
            <LanguagesFilter
              filterLanguage={filter.language}
              onChangeOtherFilterFields={onChangeOtherFilterFields}
              languages={languageList?.languages}
              refetchBanner={refetchBannerBackground}
            />
          </HeaderCol>
        </SectionHeader>
        <ClassFilter
          onChangeDaysCategoriesCities={onChangeDaysCategoriesCities}
          onChangeOtherFilterFields={onChangeOtherFilterFields}
          filter={fixFilter}
        />
        {filter.level.id ? <ClassSearchResult filter={fixFilter} /> : null}
      </BookClassSection>
    </PageContent>
  );
};

export default BookAClass;
